<template>
    <div class="container">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card style="min-height:690px">
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <span>Vistorias</span>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>Listar</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template #extra>
                        <a-button href="/download-apk">Download Aplicativo</a-button>
                    </template>

                    <div style="display:flex; justify-content:space-between; margin-bottom:20px">
                        <h2 class="h2-style" style="width:33.3%">Vistorias</h2>

                        <div style="display:flex; justify-content:space-evenly; align-items:center; width:33.3%">
                            <a-date-picker v-model:value="inspectionDateFilter" :format="'DD/MM/YYYY'" @change="applyFilters(null)" />

                            <a-dropdown v-model:open="modalVisible" placement="bottom">
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item key="1">
                                            <a-checkbox v-model:checked="filteredBySchedule" @change="applyFilters('no_schedule')">Vistorias sem agendamento</a-checkbox>
                                        </a-menu-item>
                                        <a-menu-item key="2">
                                            <a-checkbox v-model:checked="filteredByOutdate" @change="applyFilters('not_performed')">Vistorias com agendamento vencido</a-checkbox>
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                                <a-button style="width:150px;">
                                    <FilterOutlined />
                                    Filtros
                                </a-button>
                            </a-dropdown>
                        </div>

                        <div style="display:flex; justify-content:end; width:33.3%; padding-right:30px">
                            <a-button type="primary" style="text-decoration:none;"
                                      v-if="canInclude" :icon="h(PlusOutlined)" href="/inspections/create"
                            >Incluir</a-button>
                        </div>
                    </div>

                    <a-table :columns="inspectionColumns" :data-source="inspectionDataFiltered" bordered>
                        <template
                            #customFilterDropdown="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
                        >
                            <div style="padding: 8px">
                                <a-input
                                    v-if="column.dataIndex !== 'status'"
                                    ref="searchInput"
                                    :placeholder="`Procurar ${column.dataIndex}`"
                                    :value="selectedKeys[0]"
                                    style="width: 188px; margin-bottom: 8px; display: block"
                                    @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
                                    @pressEnter="handleSearch(selectedKeys, confirm, column.dataIndex)"
                                />

                                <a-checkbox-group
                                    v-else
                                    :value="selectedKeys"
                                    style="width: 188px; margin-bottom: 8px; display: flex; flex-direction: column"
                                    @change="checkedValues => setSelectedKeys(checkedValues)"
                                >
                                    <a-checkbox value="Cancelado" class="status-canceled" style="padding: 6px 4px">Cancelado</a-checkbox>
                                    <a-checkbox value="Em andamento" class="status-inProgress" style="padding: 6px 4px">Em andamento</a-checkbox>
                                    <a-checkbox value="Completado" class="status-completed" style="padding: 6px 4px">Completado</a-checkbox>
                                    <a-checkbox value="Dados migrados" class="status-migratedData" style="padding: 6px 4px">Dados migrados</a-checkbox>
                                </a-checkbox-group>

                                <a-button size="small" type="text" style="width: 90px" @click="handleReset(clearFilters, confirm)">
                                    Limpar
                                </a-button>
                                <a-button type="primary" size="small" style="width: 90px; margin-left: 8px"
                                          @click="handleSearch(selectedKeys, confirm, column.dataIndex)" :icon="h(SearchOutlined)"
                                >
                                    Procurar
                                </a-button>
                            </div>
                        </template>
                        <template #customFilterIcon="{ filtered }">
                            <search-outlined :style="{ color: filtered ? '#108ee9' : undefined }"/>
                        </template>

                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'data_agendamento'">
                                <a-tooltip :title="record.agendamento === 'pending' ? 'Data do agendamento pendente' : 'Data do último agendamento'">
                                    <span :style="{color: record.agendamento === 'pending' ? 'black' : 'grey'}">{{ record.data_agendamento }}</span>
                                </a-tooltip>
                            </template>
                            <template v-else-if="column.key === 'acao'">
                                <div class="center-flex" style="gap:5px">
                                    <a-tooltip title="Alterar" :mouse-leave-delay="0">
                                        <a-button type="primary" class="center-flex" shape="circle"
                                                  :icon="h(FormOutlined)" @click="update(record.id)"/>
                                    </a-tooltip>
                                    <a-tooltip :title="verifySchedule(record.status, record.agendamento).title" :mouse-leave-delay="0">
                                        <a-button type="primary" class="center-flex" shape="circle" :disabled="record.status === 'Completado'"
                                                  :style="{backgroundColor: verifySchedule(record.status, record.agendamento).color}" :icon="h(ScheduleOutlined)" @click="schedule(record.id)"/>
                                    </a-tooltip>
                                </div>
                            </template>
                        </template>
                    </a-table>
                </a-card>
            </a-config-provider>
        </a-spin>
    </div>
</template>

<script setup>
import {h, onMounted, reactive, ref} from 'vue';
import {FilterOutlined, FormOutlined, PlusOutlined, ScheduleOutlined, SearchOutlined} from '@ant-design/icons-vue';
import ptBR from 'ant-design-vue/es/locale/pt_BR';
import {notification} from "ant-design-vue";
import { openNotification } from '../../../utils/utilities';

const inspectionDataSource = ref();
const currentLocale = ref(ptBR);
const modalVisible = ref(false);
const spinning = ref(false);
const canInclude = ref(false);
const state = reactive({
    searchText: '',
    searchedColumn: '',
});
const searchInput = ref();
const filteredBySchedule = ref(false);
const filteredByOutdate = ref(false);
const inspectionDataFiltered = ref([]);
const inspectionDateFilter = ref('')
const inspectionColumns = ref([
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        customFilterDropdown: true,
        onFilter: (value, record) => record.id.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => {
                    searchInput.value.focus();
                }, 100);
            }
        },
        sorter: { compare: (a, b) => a.id - b.id },
        defaultSortOrder: 'descend',
        width: 100,
    },
    {
        title: 'Inscrição Imobiliária',
        dataIndex: 'matricula',
        key: 'matricula',
        customFilterDropdown: true,
        onFilter: (value, record) => record.matricula.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => {
                    searchInput.value.focus();
                }, 100);
            }
        },
        sorter: { compare: (a, b) => a.matricula - b.matricula },
        width: 300,
    },
    {
        title: 'Data e hora',
        dataIndex: 'data',
        key: 'data',
        customFilterDropdown: true,
        onFilter: (value, record) => record.data.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => {
                    searchInput.value.focus();
                }, 100);
            }
        },
        sorter: (a, b) => { return new Date(a.data).getTime() - new Date(b.data).getTime(); },
        width: 250
    },
    {
        title: 'Nº protocolo',
        dataIndex: ['protocolo', 'protocol_number'],
        key: 'protocol_number',
        customFilterDropdown: true,
        onFilter: (value, record) => record.protocolo.protocol_number.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => {
                    searchInput.value.focus();
                }, 100);
            }
        },
        sorter: (a, b) => { return new Date(a.data).getTime() - new Date(b.data).getTime(); },
        width: 200
    },
    {
        title: 'Observações',
        dataIndex: 'observacoes',
        key: 'observacoes',
        width: 600,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        filters: [
            {
                text: 'Em andamento',
                value: 'Em andamento',
            },
            {
                text: 'Completado',
                value: 'Completado',
            },
            {
                text: 'Cancelado',
                value: 'Cancelado',
            },
            {
                text: 'Dados migrados',
                value: 'Dados migrados',
            },
        ],
        customFilterDropdown: true,
        onFilter: (value, record) => record.status.indexOf(value) === 0,
        width: 250,
        customCell: (record) => {
            let bg = '';
            switch (record.status) {
                case 'Em andamento':
                    bg = '#fff7e6';
                    break;
                case 'Completado':
                    bg = '#e6f7ff';
                    break;
                case 'Cancelado':
                    bg = '#fff1f0';
                    break;
                case 'Dados migrados':
                    bg = '#f6ffed';
                    break;
            }
            return {style: {background: bg}};
        },
    },
    {
        title: 'Agendamento',
        dataIndex: 'data_agendamento',
        key: 'data_agendamento',
        customFilterDropdown: true,
        onFilter: (value, record) => record.data.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => {
                    searchInput.value.focus();
                }, 100);
            }
        },
        sorter: (a, b) => { return new Date(a.data).getTime() - new Date(b.data).getTime(); },
        width: 250
    },
    {
        title: 'Ação',
        key: 'acao',
        width: 30,
    }
]);

onMounted(async () => {
    spinning.value = true

    // id 24 - menu de inclusão de vistoria
    canInclude.value = await verifyPermission(24)

    try {
        inspectionDataSource.value = (await axios.get('/api/inspections?onlyAllowed')).data.data;
        inspectionDataFiltered.value = inspectionDataSource.value;
    } catch (error) {
        openNotification('error', 'Não foi possível carregar as vistorias.')
    }

    spinning.value = false
});
const update = (id) => {
    window.location.href = `/inspections/${id}/edit`
}

const schedule = (id) => window.location.href = `/inspections/schedule?inspection_id=${id}`

const verifyPermission = async (menuId) => {
    return (await window.axios.get(`/api/configurations/menus/${menuId}/verify-permission-from-user`)).data.data
}

const verifySchedule = (status, scheduleStatus) => {
    if (status === 'Em andamento' && scheduleStatus === 'pending') return {title: 'Reagendar', color: '#eaab24'}
    else if (status === 'Completado') return { title: 'Finalizado', color: '#108ee9' }

    else if (scheduleStatus === 'pending') return {title: 'Em andamento', color: '#eaab24'}
    else if (scheduleStatus === 'completed') return {title: 'Finalizado', color: '#108ee9'}
    else if (scheduleStatus === 'not_performed') return {title: 'Agendar novamente', color: '#ff4d4f'}
    else if (scheduleStatus === 'no_schedule') return {title: 'Agendar', color: '#52c41a'}
}

const applyFilters = (status) => {
    const date = inspectionDateFilter.value
        ? inspectionDateFilter.value.format("DD/MM/YYYY")
        : null

    const midFilter = date
        ? inspectionDataSource.value.filter(inspection => inspection.data_agendamento.includes(date))
        : inspectionDataSource.value

    inspectionDataFiltered.value = status && filteredBySchedule.value || filteredByOutdate.value
        ? midFilter.filter(inspection => inspection.agendamento === status)
        : midFilter
}
const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    state.searchText = selectedKeys;
    state.searchedColumn = dataIndex;
};
const handleReset = (clearFilters, confirm) => {
    clearFilters({
        confirm: true,
    });
    state.searchText = '';

    confirm();
};
</script>

<style scoped>
.center-flex {
    display: flex;
    justify-content: center;
    align-items: center;
}

.anticon {
    vertical-align: 0 !important;
}

.h2-style {
    padding-left: 30px;
    margin-bottom: 0;
    font-weight: bold;
    font-family: Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji
}

.status-canceled {
    background-color: #fff1f0;
    border-color: #ff4d4f;
}

.status-inProgress {
    background-color: #fff7e6;
    border-color: #ffc53d;
}

.status-completed {
    background-color: #e6f7ff;
    border-color: #1890ff;
}

.status-migratedData {
    background-color: #f6ffed;
    border-color: #52c41a;
}
</style>

